import React, { useContext, useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  Avatar,
  Typography,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import theme from "../theme";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { useNavigate, useLocation } from "react-router-dom";

import { ReactComponent as CreateStore } from "../assets/icons/create-store.svg";
import { ReactComponent as CreateDepartment } from "../assets/icons/create-department.svg";
import { ReactComponent as CreateUser } from "../assets/icons/create-user.svg";
import { ReactComponent as ReportIcon } from "../assets/icons/report.svg";
import { ReactComponent as Phone } from "../assets/icons/phone.svg";
import { ReactComponent as AboutUs } from "../assets/icons/about-us.svg";
import { ReactComponent as RateUs } from "../assets/icons/rate-us.svg";
import { ReactComponent as Logout } from "../assets/icons/logout.svg";

import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

// Web extra icons
// ================================================================
import LogoutIcon from "@mui/icons-material/Logout";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import { isManagerRole } from "../services/timerService";
import {
  canViewBranchReport,
  isServiceHead,
  isStoreHead,
} from "../utils/roles";
import { isPawnStore, isTravelStore } from "../utils/moduleNav";
import { StoreSwitcherDialog, useSwitchableStores } from "./StoreSwitcher";
import { BranchSwitcherDialog, useSwitchableBranches } from "./BranchSwitcher";

//=================================================================
import webSideBarIcon from "../assets/logo/daily-tick-header.png";
import { ReactComponent as WebSideBarWings } from "../assets/icons/header-wing.svg";
import { useMediaQuery, useTheme } from "@mui/material";
import { useSafeAreaTop, useSafeAreaBottom } from "../SafeArea";
import { Capacitor } from "@capacitor/core";

const drawerWidth = "80%";

// Icon Animation on Hover
const HoverGifIcon = ({ staticSrc, gifSrc, alt, active }) => {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        backgroundColor: "#FFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        transition: "all 0.3s ease",
      }}
    >
      <img
        src={active ? gifSrc : staticSrc}
        alt={alt}
        style={{
          width: 26,
          height: 26,
          objectFit: "contain",
          transition: "transform 0.3s ease",
          transform: active ? "scale(1.3)" : "scale(1.05)",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};

// MUI icon inside the same white circle the web menu uses (for items without a gif asset)
const CircleMuiIcon = ({ children }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      borderRadius: "50%",
      backgroundColor: "#FFF",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </Box>
);

const Sidebar = ({ open, setOpen }) => {
  const topInset = useSafeAreaTop();
  const bottomInset = useSafeAreaBottom();
  const isIOS = Capacitor.getPlatform() === "ios";

  const safeAreaTop = topInset;
  const safeAreaBottom = bottomInset;
  const location = useLocation();
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));

  const {
    userInfo,
    currentStoreInfo,
    currentBranch,
    viewingAllBranches,
    clearLoginData,
  } = useContext(AutoTaskAuthContext) || {};
  const { name = "", mobile = "", email = "", role = "" } = userInfo || {};

  // The store the user is currently signed into. Shown at the bottom of the
  // user card so it is obvious which store's data is on screen — it matters
  // once someone uses the store switcher.
  const storeName = currentStoreInfo?.name || "";

  // "KGP JEWELLERS · Hubli Branch". The branch is appended rather than given
  // its own line so it cannot push anything off a phone screen, and it is
  // dropped entirely for a service role — they are not in one branch, so a
  // chip naming one would be a lie.
  const storeLabel = [
    storeName,
    viewingAllBranches ? "All branches" : currentBranch?.name,
  ]
    .filter(Boolean)
    .join(" · ");

  // ── Store-type gating ──────────────────────────────────────────────────────
  // Each store is flagged in the store master as a pawn store (is_pawn) and/or
  // a travel store (is_travel). The sidebar only offers the module(s) the store
  // actually runs. Accounts is NOT listed for those stores — it already lives
  // inside the Travel dashboard's Masters menu and inside the pawn sidebar — so
  // it only surfaces here for plain Daily Tick stores. ATS is Kumudu-only
  // (store 0).
  const storeIsPawn = isPawnStore(currentStoreInfo);
  const storeIsTravel = isTravelStore(currentStoreInfo);
  const storeId = currentStoreInfo?.id ?? userInfo?.store_id;
  const isKumuduStore = Number(storeId) === 0;

  // The in-app store switcher. It is listed here as well as in the pawn sidebar
  // because the trip has to work both ways: someone who owns a Travel ERP store
  // and a pawn store moves between the two all day, and without an entry on this
  // side the only way back out of the store they switched into would be to log
  // out. Hidden outright when this store is connected to no other.
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const { stores: switchableStores, loading: switchableLoading } =
    useSwitchableStores();

  // The branch picker, listed beside the store one. Two separate controls
  // rather than one combined list: a store is a company and a branch is a
  // place it trades from, and somebody with two stores of three branches each
  // has to be able to say which of the two they are moving.
  //
  // `canSwitchBranches` already folds in both tests — the role may switch AND
  // this store has more than one branch — so the entry hides itself for a
  // single-branch store and for everyone pinned to a branch.
  const [branchSwitcherOpen, setBranchSwitcherOpen] = useState(false);
  const {
    branches: switchableBranches,
    loading: branchesLoading,
    canSwitch: canSwitchBranches,
    canViewAll: canViewAllBranches,
  } = useSwitchableBranches();

  const isVisibleForStore = (item) => {
    switch (item.key) {
      case "ats":
        return isKumuduStore;
      case "travel-erp":
        return storeIsTravel;
      case "pawn-shop":
        return storeIsPawn;
      case "accounts":
        return !storeIsTravel && !storeIsPawn;
      case "switch-store":
        return switchableStores.length > 0;
      case "switch-branch":
        return canSwitchBranches;
      // Nothing to compare on a single-branch store, so the entry is not
      // drawn at all — the alternative is a report that always has exactly
      // one row and a total that repeats it.
      //
      // Owner's eyes only (canViewBranchReport): it prints what every branch
      // took in, and only the person who owns all of them has a reason to read
      // that. A department head running one branch does not.
      //
      // A PAWN STORE DOES NOT GET IT HERE AT ALL. It lives inside the module's
      // own Reports screen instead (/pawn/reports?type=branch), where the rest
      // of that owner's reports already are — one door, not two.
      case "branch-report":
        return (
          switchableBranches.length > 1 &&
          canViewBranchReport(role) &&
          !storeIsPawn
        );
      default:
        return true;
    }
  };

  const navigate = useNavigate();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Icon animation effect on hover
  const [hoveredIndex, setHoveredIndex] = useState(null);

  //  Define all menu items
  const allMenuItems = [
    {
      label: "Stores",
      icon: <CreateStore />,
      link: "/all-stores",
      key: "create-store",
    },
    {
      label: "Departments",
      icon: <CreateDepartment />,
      link: "/create-department",
      key: "create-department",
    },
    {
      // The org chart of every department at once. The same screen the ATS
      // module shows at /ats/org, mounted in this shell so getting to it does
      // not mean switching module. Shares the `create-department` key so it is
      // gated to the same roles as the entry above.
      label: "Org Chart",
      icon: <AccountTreeOutlinedIcon />,
      link: "/create-department/org",
      key: "create-department",
    },
    {
      label: "User",
      icon: <PeopleOutlineOutlinedIcon />,
      link: "/create-user",
      key: "create-user",
    },
    { label: "Team", icon: <CreateUser />, link: "/team", key: "create-user" },
    {
      label: "Timesheet",
      icon: <AccessTimeOutlinedIcon />,
      link: "/timesheet",
      key: "timesheet",
    },
    {
      label: "Performance",
      icon: <SpeedRoundedIcon />,
      link: "/performance",
      key: "performance",
    },
    {
      label: "Report",
      icon: <ReportOutlinedIcon />,
      link: "/report",
      key: "report",
    },
    {
      label: "Ceo Dashboard",
      icon: <ReportOutlinedIcon />,
      link: "/ceo-dashboard",
      key: "ceo-dashboard",
    },
    {
      label: "Branch Report",
      icon: <InsightsRoundedIcon />,
      link: "/branch-report",
      key: "branch-report",
    },
    {
      label: "ATS",
      icon: <BusinessOutlinedIcon />,
      link: "/ats",
      key: "ats",
    },
    // Module entries — shown only for the store types that run them (see
    // isVisibleForStore above).
    {
      label: "Travel ERP",
      icon: <FlightTakeoffOutlinedIcon />,
      link: "/travel/dashboard",
      key: "travel-erp",
    },
    {
      label: "Pawn Shop",
      icon: <DiamondOutlinedIcon />,
      link: "/pawn/home",
      key: "pawn-shop",
    },
    // Opens the connected-store picker instead of navigating (see handleMenuClick).
    {
      label: "Switch Store",
      icon: (
        <Box
          component="img"
          src={require("../assets/icons/switch-store.png")}
          alt=""
          sx={{ width: 24, height: 24, objectFit: "contain" }}
        />
      ),
      action: () => setSwitcherOpen(true),
      key: "switch-store",
    },
    {
      label: "Switch Branch",
      icon: <PlaceOutlinedIcon />,
      action: () => setBranchSwitcherOpen(true),
      key: "switch-branch",
    },
    {
      label: "Accounts",
      icon: <AccountBalanceOutlinedIcon />,
      link: "/accounts",
      key: "accounts",
    },
    { label: "Contact Us", icon: <Phone />, key: "contact-us" },
    { label: "About Us", icon: <AboutUs />, key: "about-us" },
    { label: "Rate Us", icon: <RateUs />, key: "rate-us" },
  ];

  const webMenuItems = [
    {
      label: "Home",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/home.png")}
          gifSrc={require("../assets/icons/home.gif")}
          alt="Home"
          active={hovered}
        />
      ),
      link: "/home",
      key: "home",
    },
    {
      label: "New Ticket",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/ticket.png")}
          gifSrc={require("../assets/icons/ticket.gif")}
          alt="New Ticket"
          active={hovered}
        />
      ),
      link: "/all-service-tickets",
      key: "create-ticket",
    },
    {
      label: "My Tickets",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/tickets.png")}
          gifSrc={require("../assets/icons/tickets.gif")}
          alt="My Tickets"
          active={hovered}
        />
      ),
      link: "/mytickets",
      key: "mytickets",
    },
    {
      label: "Timesheet",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/timesheet.png")}
          gifSrc={require("../assets/icons/timesheet.gif")}
          alt="Time Sheet"
          active={hovered}
        />
      ),
      link: "/timesheet",
      key: "timesheet",
    },
    {
      label: "Project Management",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/project-management.png")}
          gifSrc={require("../assets/icons/project-management.gif")}
          alt="Project Management"
          active={hovered}
        />
      ),
      link: "/project-management",
      key: "project-management",
    },
    {
      label: "Reminder",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/bell.png")}
          gifSrc={require("../assets/icons/bell.gif")}
          alt="Reminder"
          active={hovered}
        />
      ),
      link: "/reminders",
      key: "reminders",
    },
    {
      label: "Team",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/team.png")}
          gifSrc={require("../assets/icons/team.gif")}
          alt="Team"
          active={hovered}
        />
      ),
      link: "/team",
      key: "team",
    },
    {
      label: "Performance",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/performance.png")}
          gifSrc={require("../assets/icons/performance.gif")}
          alt="Performance"
          active={hovered}
        />
      ),
      link: "/performance",
      key: "performance",
    },
    {
      label: "Report",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/checklist.png")}
          gifSrc={require("../assets/icons/checklist.gif")}
          alt="Report"
          active={hovered}
        />
      ),
      link: "/report",
      key: "report",
    },
    {
      label: "Contact Us",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/customer-service.png")}
          gifSrc={require("../assets/icons/customer-service.gif")}
          alt="Contact Us"
          active={hovered}
        />
      ),
      link: "/contact-us",
      key: "contact-us",
    },
    {
      label: "About Us",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/info.png")}
          gifSrc={require("../assets/icons/info.gif")}
          alt="About Us"
          active={hovered}
        />
      ),
      link: "/about-us",
      key: "about-us",
    },
    {
      label: "Rate Us",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/like.png")}
          gifSrc={require("../assets/icons/like.gif")}
          alt="Rate Us"
          active={hovered}
        />
      ),
      link: "/rate-us",
      key: "rate-us",
    },
    {
      label: "Stores",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/store.png")}
          gifSrc={require("../assets/icons/store.gif")}
          alt="Stores"
          active={hovered}
        />
      ),
      link: "/all-stores",
      key: "create-store",
    },
    {
      label: "CEO Dashboard",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/dashboard.png")}
          gifSrc={require("../assets/icons/dashboard.gif")}
          alt="CEO Dashboard"
          active={hovered}
        />
      ),
      link: "/ceo-dashboard",
      key: "ceo-dashboard",
    },
    {
      label: "ATS",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/ats-module.png")}
          gifSrc={require("../assets/icons/ats-module.gif")}
          alt="ATS"
          active={hovered}
        />
      ),
      link: "/ats",
      key: "ats",
    },
    {
      label: "Departments",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/departments.png")}
          gifSrc={require("../assets/icons/departments.gif")}
          alt="Departments"
          active={hovered}
        />
      ),
      link: "/create-department",
      key: "create-department",
    },
    {
      label: "Org Chart",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/org-chart.png")}
          gifSrc={require("../assets/icons/org-chart.gif")}
          alt="Org Chart"
          active={hovered}
        />
      ),
      link: "/create-department/org",
      key: "create-department",
    },
    {
      label: "User",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/profile.png")}
          gifSrc={require("../assets/icons/profile.gif")}
          alt="User"
          active={hovered}
        />
      ),
      link: "/create-user",
      key: "create-user",
    },
    {
      label: "Settings",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/settings.png")}
          gifSrc={require("../assets/icons/settings.gif")}
          alt="Settings"
          active={hovered}
        />
      ),
      link: "/store-settings",
      key: "settings",
    },
    {
      label: "Travel ERP",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/solo-traveller.png")}
          gifSrc={require("../assets/icons/solo-traveller.gif")}
          alt="Travel ERP"
          active={hovered}
        />
      ),
      link: "/travel/dashboard",
      key: "travel-erp",
    },
    {
      label: "Accounts",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/accounts-master.png")}
          gifSrc={require("../assets/icons/accounts-master.gif")}
          alt="Accounts"
          active={hovered}
        />
      ),
      link: "/accounts",
      key: "accounts",
    },
    // {
    //   label: "Jewel Master",
    //   icon: (hovered) => (
    //     <HoverGifIcon
    //       staticSrc={require("../assets/icons/jewel-master.png")}
    //       gifSrc={require("../assets/icons/jewel-master.gif")}
    //       alt="Jewel Master"
    //       active={hovered}
    //     />
    //   ),
    //   link: "/jewel-master/home",
    //   key: "jewel-master",
    // },
    {
      label: "Pawn Shop",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/pawn-shop.png")}
          gifSrc={require("../assets/icons/pawn-shop.gif")}
          alt="Pawn Shop"
          active={hovered}
        />
      ),
      link: "/pawn/home",
      key: "pawn-shop",
    },
    // Opens the connected-store picker instead of navigating (see handleMenuClick).
    {
      label: "Switch Store",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/switch-store.png")}
          gifSrc={require("../assets/icons/switch-store.gif")}
          alt="Switch Store"
          active={hovered}
        />
      ),
      action: () => setSwitcherOpen(true),
      key: "switch-store",
    },
    {
      label: "Switch Branch",
      icon: (hovered) => (
        <HoverGifIcon
          staticSrc={require("../assets/icons/switch-branch.png")}
          gifSrc={require("../assets/icons/switch-branch.gif")}
          alt="Switch Branch"
          active={hovered}
        />
      ),
      action: () => setBranchSwitcherOpen(true),
      key: "switch-branch",
    },
    {
      // A plain icon rather than the hover-gif pair the rail's other entries
      // use: there is no artwork for this one, and the renderer already falls
      // back to an element when the icon is not a function.
      label: "Branch Report",
      icon: <InsightsRoundedIcon sx={{ fontSize: 28 }} />,
      link: "/branch-report",
      key: "branch-report",
    },
  ];

  // 🔹 Role-based filtering (store-type gating is applied on top of it, so a
  // Service Head still doesn't see Pawn Shop on a travel-only store).
  const isVisibleForRole = (item) => {
    if (item.managerOnly && !isManagerRole(role)) return false;
    if (isServiceHead(role)) return true;
    if (isStoreHead(role)) return item.key !== "create-store";
    // hide create-store, create-department, and create-user for others
    return !["create-store", "create-department", "create-user"].includes(
      item.key,
    );
  };

  const isVisible = (item) => isVisibleForRole(item) && isVisibleForStore(item);

  const filteredMenuItems = allMenuItems.filter(isVisible);

  const filteredWebMenuItem = webMenuItems.filter(isVisible);

  const handleMenuClick = (item) => {
    // Action items (the store switcher) do their work in place rather than
    // navigating, so they are handled before the link is required.
    if (item?.action) {
      if (isMobile) setOpen(false);
      item.action();
      return;
    }

    if (!item?.link) return;

    if (isMobile) {
      setOpen(false);
    }

    if (item.key === "travel-erp") {
      const alreadyShown = sessionStorage.getItem("travelSplashShown");

      navigate(alreadyShown ? "/travel/dashboard" : "/travel/splash");
      return;
    }

    if (item.key === "jewel-master") {
      const alreadyShown = sessionStorage.getItem("jewelMasterSplashShown");

      navigate(alreadyShown ? "/jewel-master/home" : "/jewel-master/splash");
      return;
    }

    navigate(item.link);
  };

  const handleConfirmLogout = async () => {
    await clearLoginData?.();
    setLogoutModalOpen(false);
    // `replace`: the page we are leaving belongs to a session that no longer
    // exists, so back must not walk into it. Same rule as the shared
    // LogoutConfirmDialog the pawn and travel hosts use.
    navigate("/", { replace: true });
  };

  if (isMobile) {
    // Mobile Sidebar
    return (
      <>
        <Drawer
          variant="temporary"
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            display: "flex",
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              zIndex: 5500,
              mt: isIOS ? "72px" : `calc(72px + ${safeAreaTop})`,
              pb: "65px",
            },
          }}
        >
          {/* User Info — pinned at the top */}
          <Box sx={{ px: 2, pt: 2, flexShrink: 0 }}>
            <Box
              sx={{
                background:
                  "linear-gradient(90deg,rgba(0, 173, 173, 1) 0%, rgba(68, 40, 160, 1) 100%)",
                borderRadius: 2,
                p: 1,
                minHeight: "100px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ width: "100%" }}
              >
                <Avatar
                  sx={{
                    bgcolor: "#fff",
                    color: theme.textCol.primaryHeading,
                    fontSize: 24,
                    width: 56,
                    height: 56,
                  }}
                >
                  {name?.[0] || "U"}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontSize={14} fontWeight="400" color="#fff">
                    {name}
                  </Typography>
                  <Typography fontSize={14} color="#fff">
                    {mobile}
                  </Typography>
                  <Box
                    sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
                  >
                    <Typography
                      fontSize={14}
                      color="#fff"
                      noWrap
                      sx={{
                        minWidth: 0,
                        flexGrow: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {email}
                    </Typography>
                  </Box>
                  {storeName && (
                    <Typography
                      fontSize={13}
                      fontWeight="500"
                      color="#fff"
                      noWrap
                      sx={{
                        opacity: 0.85,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {storeLabel}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider />

          {/* Menu — the only scrolling region, so Log out below stays put */}
          <List
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {filteredMenuItems.map((item, index) => (
              <ListItemButton
                key={index}
                sx={{ px: 2, py: 1.5 }}
                onClick={() => handleMenuClick(item)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                <Box>
                  <Typography fontSize={14}>{item.label}</Typography>
                  {item.subheading && (
                    <Typography fontSize={12} color="text.secondary">
                      {item.subheading}
                    </Typography>
                  )}
                </Box>
              </ListItemButton>
            ))}
          </List>

          {/* Footer — pinned to the bottom */}
          <Box sx={{ flexShrink: 0 }} textAlign="center" mb={1}>
            <Box sx={{ width: "100%", p: 2 }}>
              <Typography
                onClick={() => setLogoutModalOpen(true)}
                sx={{
                  borderRadius: 2,
                  border: "2px solid #008080",
                  height: 50,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#008080",
                  gap: 2,
                  cursor: "pointer",
                }}
              >
                <Logout /> Log out
              </Typography>
            </Box>
            <Typography fontSize={11} color="text.secondary">
              Version - 101.01
            </Typography>
          </Box>
        </Drawer>

        {/* Connected-store picker, opened from the Switch Store entry */}
        <StoreSwitcherDialog
          open={switcherOpen}
          onClose={() => setSwitcherOpen(false)}
          stores={switchableStores}
          loading={switchableLoading}
        />

        {/* Branch picker, opened from the Switch Branch entry */}
        <BranchSwitcherDialog
          open={branchSwitcherOpen}
          onClose={() => setBranchSwitcherOpen(false)}
          branches={switchableBranches}
          loading={branchesLoading}
          canViewAll={canViewAllBranches}
          viewingAll={viewingAllBranches}
        />

        {/* Logout Modal */}
        <Dialog
          open={logoutModalOpen}
          onClose={() => setLogoutModalOpen(false)}
          aria-labelledby="logout-dialog-title"
          aria-describedby="logout-dialog-description"
        >
          <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
          <DialogContent>
            <Typography id="logout-dialog-description">
              Are you sure you want to log out?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ color: "#008080" }}
              onClick={() => setLogoutModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmLogout}
              variant="contained"
              sx={{
                background:
                  "linear-gradient(90deg,rgba(0, 173, 173, 1) 0%, rgba(68, 40, 160, 1) 100%)",
              }}
            >
              Logout
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  } else {
    // Web Sidebar
    return (
      <>
        {/* Overlay */}
        <Box
          onClick={() => setIsHovered(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 4900,
            pointerEvents: isHovered ? "auto" : "none",
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        />

        {/* Sidebar */}
        <Box
          sx={{
            maxWidth: "300px",
            width: isHovered ? "30%" : "56px",
            transition: "width 0.3s ease",
            height: "100vh",
            background:
              "linear-gradient(180deg,rgba(3, 158, 158, 1) 0%, rgba(68, 40, 160, 1) 100%)",
            position: "fixed",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 5000,
            color: "#fff",
            overflow: "hidden",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Logo/Header */}
          <Box
            sx={{
              // pt: 2,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "56px",
              transition: "all 0.3s ease",
            }}
          >
            {isHovered ? (
              <img
                src={webSideBarIcon}
                alt="Sidebar Logo"
                style={{
                  height: "24px",
                  objectFit: "contain",
                  transition: "all 0.3s ease",
                }}
              />
            ) : (
              <WebSideBarWings
                style={{
                  height: "24px",
                  transition: "all 0.3s ease",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              pt: 1,
              px: 0.8,
              // minHeight, not height: the card now carries a 4th line (store
              // name) and the parent clips overflow.
              minHeight: "80px",
              width: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                height: "100%",
                borderRadius: 2,
                paddingLeft: 0.5,
                width: "100%",
                bgcolor: isHovered ? "rgb(255,255,255,0.9)" : "transparent",
                transition: "background-color 0.3s ease",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: isHovered ? "#1797A6" : "#fff",
                  color: isHovered ? "#fff" : theme.textCol.primaryHeading,
                  fontSize: 22,
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  transition: "all 0.3s ease",
                }}
              >
                {name?.[0] || "U"}
              </Avatar>

              {/* Text block (always rendered, but animated) */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  opacity: isHovered ? 1 : 0,
                  maxWidth: isHovered ? "200px" : 0, // collapses smoothly
                  transform: isHovered ? "translateX(0)" : "translateX(-8px)",
                  transition: "all 0.3s ease",
                }}
              >
                <Typography
                  fontSize={14}
                  fontWeight="400"
                  color={isHovered ? "#066B6B" : "#fff"}
                  noWrap
                >
                  {name}
                </Typography>
                <Typography
                  fontSize={14}
                  color={isHovered ? "#066B6B" : "#fff"}
                  noWrap
                >
                  {mobile}
                </Typography>
                <Typography
                  fontSize={14}
                  color={isHovered ? "#066B6B" : "#fff"}
                  noWrap
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {email}
                </Typography>
                {storeName && (
                  <Typography
                    fontSize={13}
                    fontWeight="500"
                    color={isHovered ? "#066B6B" : "#fff"}
                    noWrap
                    sx={{
                      opacity: 0.85,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {storeLabel}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>

          {/* Menu Items — the ONLY scrolling region. The logo + user card above
              and the logout footer below stay pinned, so a long menu never
              pushes Log out off screen. */}
          <Box
            sx={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              // Vertical only. Left implicit, `overflow-y: auto` makes the
              // browser compute overflow-x to `auto` as well, so a swipe
              // inside the collapsed rail scrolled the icons sideways.
              overflowX: "hidden",
              overscrollBehaviorX: "contain",

              /* Hide scrollbar */
              scrollbarWidth: "none", // Firefox
              msOverflowStyle: "none", // IE/Edge
              "&::-webkit-scrollbar": {
                display: "none", // Chrome, Safari
              },
            }}
          >
            <List sx={{ width: "100%" }}>
              {filteredWebMenuItem.map((item, index) => {
                const isItemHovered = hoveredIndex === index;

                return (
                  <ListItemButton
                    key={index}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuClick(item);
                    }}
                    sx={{
                      color: "#fff",
                      justifyContent: isHovered ? "flex-start" : "flex-start",
                      transition: "all 0.3s ease",
                      backgroundColor:
                        location.pathname === item.link
                          ? "rgba(0, 0, 0, 0.15)"
                          : "transparent",
                      borderLeft:
                        location.pathname === item.link
                          ? "4px solid rgba(0, 0, 0, 0.8)"
                          : "4px solid transparent",
                      px: 0,
                      py: 1,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        // Expanded: a fixed leading column. Collapsed: fill the
                        // rail (56px less the 4px active-item border) so the
                        // icon stays centred instead of being pushed out of it.
                        width: isHovered ? 56 : "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#fff",
                        px: 0,
                        py: 0,
                      }}
                    >
                      {typeof item.icon === "function"
                        ? item.icon(isItemHovered)
                        : item.icon}
                    </ListItemIcon>

                    <Typography
                      fontSize={18}
                      sx={{
                        opacity: isHovered ? 1 : 0,
                        whiteSpace: "nowrap",
                        // Collapse the label out of the layout, don't just fade
                        // it: a nowrap label still claims its full width, which
                        // is what made every row wider than the 56px rail.
                        minWidth: 0,
                        maxWidth: isHovered ? "200px" : 0,
                        overflow: "hidden",
                        transition: "opacity 0.3s ease, max-width 0.3s ease",
                        fontWeight: location.pathname === item.link ? 600 : 400,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          {/* Footer (Logout + Version) — pinned to the bottom of the rail.
              Collapsed it shrinks to the same white circle the menu icons use,
              so logging out stays reachable without opening the sidebar;
              expanded it grows back into the full "Log out" bar. */}
          <Box
            textAlign="center"
            mb={2}
            width="100%"
            sx={{
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              onClick={(e) => {
                e.stopPropagation();
                setLogoutModalOpen(true);
              }}
              title="Log out"
              sx={{
                mt: isHovered ? 1.5 : 2.5,
                boxSizing: "border-box",
                flexShrink: 0,
                width: isHovered ? "calc(100% - 32px)" : 40,
                height: isHovered ? 44 : 40,
                minHeight: isHovered ? 44 : 40,
                borderRadius: isHovered ? 2 : "50%",
                border: "2px solid #fff",
                backgroundColor: isHovered ? "transparent" : "#FFF",
                color: isHovered ? "#fff" : theme.textCol.primaryHeading,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: isHovered ? 1 : 0,
                overflow: "hidden",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              <LogoutIcon sx={{ fontSize: 22, flexShrink: 0 }} />
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  opacity: isHovered ? 1 : 0,
                  maxWidth: isHovered ? "140px" : 0,
                  transition: "all 0.3s ease",
                }}
              >
                Log out
              </Typography>
            </Box>
            <Typography
              fontSize={11}
              sx={{
                mt: 1,
                opacity: isHovered ? 1 : 0,
                transition: "opacity 0.3s ease",
              }}
              color="rgba(255,255,255,0.7)"
            >
              Version - 101.01
            </Typography>
          </Box>
        </Box>

        {/* Connected-store picker, opened from the Switch Store entry */}
        <StoreSwitcherDialog
          open={switcherOpen}
          onClose={() => setSwitcherOpen(false)}
          stores={switchableStores}
          loading={switchableLoading}
        />

        {/* Branch picker, opened from the Switch Branch entry */}
        <BranchSwitcherDialog
          open={branchSwitcherOpen}
          onClose={() => setBranchSwitcherOpen(false)}
          branches={switchableBranches}
          loading={branchesLoading}
          canViewAll={canViewAllBranches}
          viewingAll={viewingAllBranches}
        />

        {/* Logout Modal */}
        <Dialog
          open={logoutModalOpen}
          onClose={() => setLogoutModalOpen(false)}
          aria-labelledby="logout-dialog-title"
          aria-describedby="logout-dialog-description"
        >
          <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
          <DialogContent>
            <Typography id="logout-dialog-description">
              Are you sure you want to log out?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ color: "#008080" }}
              onClick={() => setLogoutModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmLogout}
              variant="contained"
              sx={{
                background:
                  "linear-gradient(90deg,rgba(0, 173, 173, 1) 0%, rgba(68, 40, 160, 1) 100%)",
              }}
            >
              Logout
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
};

export default Sidebar;
